import type { CaseCard } from "../verifications/attributionService.model.ts";
import type { RequestCard } from "../verifications/verifications.model.ts";
import type { CatalogueRow } from "./catalogue.model.ts";
import { AUTHENTICATIONS_COPY } from "./authentications.constants.ts";

/**
 * The Authentication Requests screen's logic — OND-212.
 *
 * NOTHING HERE IS A NEW RULE. The inbox decides who may answer, the service
 * decides what a case's stage and target are, and the catalogue decides what
 * is confirmed; this file only narrows each of those lists to the authorship
 * claim and lays them beside one another. A rule that existed only here would
 * be a second place the same question was answered, and the two would drift.
 */

/** The authorship claims, out of everything the inbox holds. */
export function authorshipRequests(rows: readonly RequestCard[]): RequestCard[] {
  return rows.filter((row) => row.kind === "attribution");
}

/** Split into what is waiting on the estate and what it has already said. */
export function splitAwaiting(rows: readonly RequestCard[]): {
  awaiting: RequestCard[];
  answered: RequestCard[];
} {
  return {
    awaiting: rows.filter((row) => row.awaiting),
    answered: rows.filter((row) => !row.awaiting),
  };
}

/** The studio's side of OND-60's cases, live and settled. */
export function studioCases(cases: readonly CaseCard[]): CaseCard[] {
  return cases.filter((card) => card.studioSide);
}

/** The case behind one request, if the service knows of it. */
export function caseFor(
  cases: readonly CaseCard[],
  requestId: string,
): CaseCard | null {
  return cases.find((card) => card.requestId === requestId) ?? null;
}

/**
 * One line under an awaiting request: stage, target or overdue, who holds it.
 *
 * The service's facts, not the inbox's — so a reviewer answering from here
 * sees the target they are working to without opening the second section.
 */
export function serviceStrip(card: CaseCard | null): string | null {
  if (card === null) return null;
  const parts: string[] = [card.stageLabel];
  if (card.overdue) parts.push(AUTHENTICATIONS_COPY.stripOverdue);
  else if (card.target) parts.push(`${AUTHENTICATIONS_COPY.stripTarget} ${card.target}`);
  parts.push(
    card.reviewer
      ? `${AUTHENTICATIONS_COPY.stripWith} ${card.reviewer}`
      : AUTHENTICATIONS_COPY.stripUnassigned,
  );
  if (card.latestEvidenceReason) parts.push(AUTHENTICATIONS_COPY.stripEvidence);
  return parts.join(" · ");
}

/** The works this estate has confirmed, out of everything named for its artist. */
export function confirmedRows(rows: readonly CatalogueRow[]): CatalogueRow[] {
  return rows.filter((row) => row.attested);
}
